(function () {
  'use strict';

  if (customElements.get('ai-pdp-purchase-options-picker')) return;

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function positiveNumber(value, fallback) {
    var number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : fallback;
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = precision == null ? 2 : precision;
    thousands = thousands == null ? ',' : thousands;
    decimal = decimal == null ? '.' : decimal;
    if (number == null || Number.isNaN(number)) return '0';
    var fixed = (number / 100).toFixed(precision);
    var parts = fixed.split('.');
    parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    return parts[1] ? parts[0] + decimal + parts[1] : parts[0];
  }

  class AIPDPPurchaseOptionsPicker extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.adapter = (window.AIPDPThemeAdapters && window.AIPDPThemeAdapters.shrine) || null;
      this.config = this.readConfig();
      this.variants = Array.isArray(this.config.variants) ? this.config.variants : [];
      this.variantMap = new Map(this.variants.map(function (variant) {
        return [String(variant.id), variant];
      }));
      this.form = null;
      this.formSubmitHandler = this.onFormSubmit.bind(this);
      this.submitting = false;
      this.lastVariantId = 0;
      this.touchedOfferVariants = new Set();
      this.lastDialogTrigger = null;
      this.bodyOverflow = '';

      this.ensureForm();
      this.bindEvents();
      this.initializeOffers();
      this.syncAllAddons();
      this.syncMainVariant(true);
      this.pollTimer = window.setInterval(this.poll.bind(this), 350);
    }

    disconnectedCallback() {
      if (!this._connected) return;
      this._connected = false;
      if (this.pollTimer) window.clearInterval(this.pollTimer);
      if (this.form) this.form.removeEventListener('submit', this.formSubmitHandler, true);
      if (this.form) {
        toArray(this.form.querySelectorAll('[data-ai-ppo-native-property]')).forEach(function (input) {
          input.remove();
        });
      }
      if (this.adapter) {
        this.adapter.setDynamicCheckoutHidden(this, this.form, false);
      }
      this.closeDialog();
      if (this.abortController) this.abortController.abort();
    }

    readConfig() {
      var script = this.querySelector('[data-ai-ppo-config]');
      if (!script) return {};
      try {
        return JSON.parse(script.textContent || '{}');
      } catch (error) {
        return {};
      }
    }

    query(selector, scope) {
      return (scope || this).querySelector(selector);
    }

    queryAll(selector, scope) {
      return toArray((scope || this).querySelectorAll(selector));
    }

    bindEvents() {
      var options = { signal: this.signal };
      this.addEventListener('pointerdown', this.onPointerDown.bind(this), {
        capture: true,
        signal: this.signal
      });
      this.addEventListener('click', this.onClick.bind(this), options);
      this.addEventListener('change', this.onChange.bind(this), options);
      document.addEventListener('click', this.onDocumentClick.bind(this), {
        capture: true,
        signal: this.signal
      });
      document.addEventListener('change', this.onDocumentChange.bind(this), {
        capture: true,
        signal: this.signal
      });
      ['variant:change', 'variant-change', 'product:variant-change', 'product:variant-changed'].forEach(function (name) {
        document.addEventListener(name, this.deferVariantSync.bind(this), {
          signal: this.signal
        });
      }, this);
      window.addEventListener('pageshow', this.onPageShow.bind(this), options);
      document.addEventListener('shopify:block:select', this.onBlockSelect.bind(this), options);

      var dialog = this.query('[data-ai-ppo-dialog]');
      if (dialog) {
        dialog.addEventListener('click', function (event) {
          if (event.target === dialog) this.closeDialog();
        }.bind(this), options);
        dialog.addEventListener('close', this.afterDialogClose.bind(this), options);
      }
    }

    initializeOffers() {
      var offers = this.queryAll('[data-ai-ppo-offer]');
      if (!offers.length) return;
      var active = offers.find(function (offer) {
        return offer.getAttribute('data-default') === 'true';
      }) || offers[0];
      this.selectOffer(active.getAttribute('data-offer-key'), false);
    }

    ensureForm() {
      if (!this.adapter) return null;
      var nextForm = this.adapter.findProductForm(this, this.getAttribute('data-product-form-id') || '');
      if (nextForm === this.form) return this.form;
      if (this.form) this.form.removeEventListener('submit', this.formSubmitHandler, true);
      this.form = nextForm;
      if (this.form) this.form.addEventListener('submit', this.formSubmitHandler, true);
      return this.form;
    }

    poll() {
      if (!this._connected) return;
      this.ensureForm();
      this.syncMainVariant(false);
    }

    deferVariantSync() {
      window.setTimeout(function () {
        this.syncMainVariant(false);
      }.bind(this), 0);
    }

    onDocumentChange(event) {
      if (!this.form) return;
      if (this.form.contains(event.target) || event.target.name === 'id') this.deferVariantSync();
    }

    onPageShow() {
      this.setSubmitting(false);
      this.showMessage('', '');
      this.ensureForm();
      this.syncMainVariant(true);
    }

    onBlockSelect(event) {
      if (event.target && (event.target === this || event.target.contains(this))) {
        var active = this.activeOffer();
        if (active) active.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }

    onPointerDown(event) {
      var button = event.target.closest('[data-ai-ppo-variant-button]');
      if (button && this.contains(button) && !button.disabled && event.button !== 2) {
        this.selectVariantButton(button, event);
      }
    }

    onClick(event) {
      var offerButton = event.target.closest('[data-ai-ppo-offer-toggle]');
      if (offerButton) {
        event.preventDefault();
        this.selectOffer(offerButton.getAttribute('data-offer-key'), true);
        return;
      }

      var variantButton = event.target.closest('[data-ai-ppo-variant-button]');
      if (variantButton && !variantButton.disabled) {
        this.selectVariantButton(variantButton, event);
        return;
      }

      var quantityButton = event.target.closest('[data-ai-ppo-qty-change]');
      if (quantityButton && !quantityButton.disabled) {
        event.preventDefault();
        this.changeAddonQuantity(quantityButton);
        return;
      }

      var preview = event.target.closest('[data-ai-ppo-preview]');
      if (preview && !preview.disabled) {
        event.preventDefault();
        this.openDialog(preview);
        return;
      }

      if (event.target.closest('[data-ai-ppo-dialog-close]')) {
        event.preventDefault();
        this.closeDialog();
      }
    }

    onChange(event) {
      var mainOptionSelect = event.target.closest('[data-ai-ppo-main-option-select]');
      if (mainOptionSelect) {
        this.syncMainVariantSelection(mainOptionSelect);
        return;
      }
      var select = event.target.closest('[data-ai-ppo-variant-select]');
      if (select) {
        this.syncAddonVariant(select.closest('[data-ai-ppo-addon]'));
        return;
      }
      var checkbox = event.target.closest('[data-ai-ppo-addon-toggle]');
      if (checkbox) this.syncAddonState(checkbox.closest('[data-ai-ppo-addon]'));
    }

    onDocumentClick(event) {
      if (this.submitting || !this.form || !this.requiresMultiItemSubmission()) return;
      var paymentMethod = this.quickPaymentMethod(event.target);
      if (paymentMethod) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.submitMultiItem(paymentMethod);
        return;
      }
      var submitter = event.target.closest([
        'button[name="add"]',
        'input[name="add"]',
        '[data-add-to-cart]',
        '[id^="SectionAtcBtn-"]'
      ].join(','));
      if (!submitter) return;
      var belongsToForm = this.form.contains(submitter);
      var shrineStickyButton = submitter.id && submitter.id.indexOf('SectionAtcBtn-') === 0;
      if (!belongsToForm && !shrineStickyButton) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.submitMultiItem();
    }

    quickPaymentMethod(target) {
      if (!target || typeof target.closest !== 'function') return '';
      var entry = target.closest([
        '[data-sg-atc][data-sg-payment]',
        'button[name="QuickCartByPP"]',
        'button[name="QuickCart"]',
        'apple-pay-button',
        '.apple-pay-btn'
      ].join(','));
      if (!entry) return '';
      var explicit = (entry.getAttribute('data-sg-payment') || '').toLowerCase();
      if (explicit === 'card' || explicit === 'paypal' || explicit === 'apple') return explicit;
      if (entry.matches('button[name="QuickCartByPP"]')) return 'paypal';
      if (entry.matches('button[name="QuickCart"]')) return 'card';
      if (entry.matches('apple-pay-button, .apple-pay-btn')) return 'apple';
      return '';
    }

    onFormSubmit(event) {
      if (this.submitting) {
        event.preventDefault();
        return;
      }
      this.syncNativeQuantity();
      if (!this.requiresMultiItemSubmission()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.submitMultiItem();
    }

    activeOffer() {
      return this.query('[data-ai-ppo-offer].is-active');
    }

    offerDimensions(offer) {
      var purchasedTier = positiveNumber(
        offer && (offer.getAttribute('data-purchased-tier') || offer.getAttribute('data-quantity')),
        1
      );
      var physicalUnits = positiveNumber(offer && offer.getAttribute('data-physical-units'), purchasedTier);
      var cartQuantity = positiveNumber(offer && offer.getAttribute('data-cart-quantity'), physicalUnits);
      var freeUnits = Math.max(0, Number(offer && offer.getAttribute('data-free-units')) || 0);
      var variantMode = String(offer && offer.getAttribute('data-variant-mode') || 'per_unit_variant');
      if (variantMode !== 'shared_variant') variantMode = 'per_unit_variant';
      return {
        purchasedTier: purchasedTier,
        physicalUnits: physicalUnits,
        cartQuantity: cartQuantity,
        freeUnits: Math.min(freeUnits, physicalUnits),
        variantMode: variantMode
      };
    }

    offerProperties(offer) {
      var dimensions = this.offerDimensions(offer);
      var properties = {
        Offer: String(offer && offer.getAttribute('data-offer-label') || offer && offer.getAttribute('data-offer-key') || 'Offer'),
        'Purchased tier': String(dimensions.purchasedTier),
        'Physical units': String(dimensions.physicalUnits),
        'Free units': String(dimensions.freeUnits)
      };
      var bonus = String(offer && offer.getAttribute('data-offer-bonus') || '').trim();
      var shipping = String(offer && offer.getAttribute('data-offer-shipping') || '').trim();
      if (bonus) properties.Bonus = bonus;
      if (shipping) properties['Offer shipping'] = shipping;
      return properties;
    }

    cloneProperties(properties) {
      return Object.keys(properties || {}).reduce(function (copy, key) {
        copy[key] = properties[key];
        return copy;
      }, {});
    }

    selectOffer(key, focus) {
      var selected = null;
      this.queryAll('[data-ai-ppo-offer]').forEach(function (offer) {
        var isActive = offer.getAttribute('data-offer-key') === key;
        offer.classList.toggle('is-active', isActive);
        var toggle = this.query('[data-ai-ppo-offer-toggle]', offer);
        var mainVariants = this.query('[data-ai-ppo-main-variants]', offer);
        var addons = this.query('[data-ai-ppo-addons]', offer);
        if (toggle) toggle.setAttribute('aria-checked', isActive ? 'true' : 'false');
        if (mainVariants) mainVariants.hidden = !isActive;
        if (addons) addons.hidden = !isActive;
        if (isActive) selected = offer;
      }, this);
      if (!selected) return;
      this.syncNativeQuantity();
      this.syncOfferPrices();
      this.syncDynamicCheckout();
      this.syncAddonSummary(this.query('[data-ai-ppo-addons]', selected));
      if (focus) {
        var button = this.query('[data-ai-ppo-offer-toggle]', selected);
        if (button) button.focus({ preventScroll: true });
      }
    }

    currentVariantId() {
      if (this.adapter && this.form) {
        var formVariant = this.adapter.getVariantId(this.form);
        if (formVariant) return formVariant;
      }
      return Number(this.config.initialVariantId) || 0;
    }

    currentVariant() {
      var id = this.currentVariantId();
      return this.variantMap.get(String(id)) || this.variants.find(function (variant) {
        return variant.available;
      }) || this.variants[0] || null;
    }

    mainVariantRows(offer) {
      return offer ? this.queryAll('[data-ai-ppo-main-variant-row]', offer) : [];
    }

    mainOptionSelects(row) {
      return row ? this.queryAll('[data-ai-ppo-main-option-select]', row) : [];
    }

    variantOptions(variant) {
      return variant && Array.isArray(variant.options) ? variant.options : [];
    }

    availableVariants() {
      return this.variants.filter(function (variant) {
        return variant && variant.available !== false;
      });
    }

    normalizeOption(value) {
      return String(value == null ? '' : value).trim().toLowerCase();
    }

    rowOptions(row) {
      return this.mainOptionSelects(row).map(function (select) {
        return select.value;
      });
    }

    variantForOptions(options) {
      return this.availableVariants().find(function (variant) {
        var values = this.variantOptions(variant);
        return options.length === values.length && options.every(function (value, index) {
          return this.normalizeOption(value) === this.normalizeOption(values[index]);
        }, this);
      }, this) || null;
    }

    compatibleVariantForChange(row, optionIndex, value) {
      var proposed = this.rowOptions(row);
      proposed[optionIndex] = value;
      var exact = this.variantForOptions(proposed);
      if (exact) return exact;
      return this.availableVariants().find(function (variant) {
        var options = this.variantOptions(variant);
        if (this.normalizeOption(options[optionIndex]) !== this.normalizeOption(value)) return false;
        for (var index = 0; index < optionIndex; index += 1) {
          if (proposed[index] && this.normalizeOption(options[index]) !== this.normalizeOption(proposed[index])) return false;
        }
        return true;
      }, this) || null;
    }

    optionValueAvailable(row, optionIndex, value) {
      var selected = this.rowOptions(row);
      return this.availableVariants().some(function (variant) {
        var options = this.variantOptions(variant);
        if (this.normalizeOption(options[optionIndex]) !== this.normalizeOption(value)) return false;
        for (var index = 0; index < optionIndex; index += 1) {
          if (selected[index] && this.normalizeOption(options[index]) !== this.normalizeOption(selected[index])) return false;
        }
        return true;
      }, this);
    }

    refreshMainRowAvailability(row) {
      this.mainOptionSelects(row).forEach(function (select, optionIndex) {
        toArray(select.options).forEach(function (option) {
          var label = option.getAttribute('data-option-label') || option.value;
          var available = this.optionValueAvailable(row, optionIndex, option.value);
          option.disabled = !available;
          option.textContent = label + (available ? '' : ' — Sold out');
        }, this);
      }, this);
    }

    setMainRowVariant(row, variant) {
      if (!row || !variant || variant.available === false) return false;
      var options = this.variantOptions(variant);
      this.mainOptionSelects(row).forEach(function (select, index) {
        var match = toArray(select.options).find(function (option) {
          return this.normalizeOption(option.value) === this.normalizeOption(options[index]);
        }, this);
        if (match) select.value = match.value;
      }, this);
      row.setAttribute('data-selected-variant-id', String(variant.id));
      this.refreshMainRowAvailability(row);
      return true;
    }

    selectedMainVariants(offer) {
      return this.mainVariantRows(offer).map(function (row) {
        var id = Number(row.getAttribute('data-selected-variant-id')) || 0;
        return this.variantMap.get(String(id)) || null;
      }, this).filter(function (variant) {
        return variant && variant.available !== false;
      });
    }

    syncOfferVariantDefaults(variantId, force) {
      if (!variantId) return;
      this.queryAll('[data-ai-ppo-offer]').forEach(function (offer) {
        var key = offer.getAttribute('data-offer-key') || '';
        if (!force && this.touchedOfferVariants.has(key)) return;
        var variant = this.variantMap.get(String(variantId));
        if (!variant || variant.available === false) variant = this.availableVariants()[0] || null;
        this.mainVariantRows(offer).forEach(function (row) {
          this.setMainRowVariant(row, variant);
        }, this);
      }, this);
    }

    syncMainVariantSelection(select) {
      var offer = select && select.closest('[data-ai-ppo-offer]');
      var row = select && select.closest('[data-ai-ppo-main-variant-row]');
      if (!offer || !row) return;
      var optionIndex = Number(select.getAttribute('data-option-index')) || 0;
      var previous = this.variantMap.get(String(row.getAttribute('data-selected-variant-id') || '')) || null;
      var variant = this.compatibleVariantForChange(row, optionIndex, select.value);
      if (!variant) {
        if (previous) this.setMainRowVariant(row, previous);
        this.showMessage(this.config.unavailableText || 'Choose an available product option before adding to cart.', 'error');
        return;
      }
      this.setMainRowVariant(row, variant);
      var key = offer.getAttribute('data-offer-key') || '';
      this.touchedOfferVariants.add(key);
      if (!offer.classList.contains('is-active')) this.selectOffer(key, false);
      this.showMessage('', '');
      this.syncOfferPrices();
      this.syncNativeQuantity();
    }

    requiresMultiItemSubmission() {
      var offer = this.activeOffer();
      return this.activeAddonItems().length > 0 || this.mainVariantRows(offer).length > 0;
    }

    syncMainVariant(force) {
      var id = this.currentVariantId();
      if (!force && id === this.lastVariantId) return;
      this.lastVariantId = id;
      this.syncOfferVariantDefaults(id, force);
      this.syncOfferPrices();
      this.syncNativeQuantity();
    }

    syncNativeQuantity() {
      var offer = this.activeOffer();
      if (!offer || !this.form || !this.adapter) return;
      this.adapter.setQuantity(this.form, this.offerDimensions(offer).cartQuantity);
      this.syncNativeProperties(offer);
    }

    syncNativeProperties(offer) {
      if (!this.form || !offer) return;
      var properties = this.offerProperties(offer);
      var activeNames = new Set();
      Object.keys(properties).forEach(function (key) {
        var name = 'properties[' + key + ']';
        activeNames.add(name);
        var input = toArray(this.form.querySelectorAll('[data-ai-ppo-native-property]')).find(function (item) {
          return item.name === name;
        });
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.setAttribute('data-ai-ppo-native-property', '');
          this.form.appendChild(input);
        }
        input.value = properties[key];
      }, this);
      toArray(this.form.querySelectorAll('[data-ai-ppo-native-property]')).forEach(function (input) {
        if (!activeNames.has(input.name)) input.remove();
      });
    }

    money(cents) {
      cents = Math.round(Number(cents) || 0);
      var format = this.config.moneyFormat || '${{amount}}';
      var match = format.match(/\{\{\s*(amount(?:_no_decimals|_with_comma_separator|_no_decimals_with_comma_separator|_with_apostrophe_separator)?)\s*\}\}/);
      if (!match) return formatWithDelimiters(cents, 2, ',', '.');
      var placeholder = match[1];
      var value;
      if (placeholder === 'amount_no_decimals') value = formatWithDelimiters(cents, 0, ',', '.');
      else if (placeholder === 'amount_with_comma_separator') value = formatWithDelimiters(cents, 2, '.', ',');
      else if (placeholder === 'amount_no_decimals_with_comma_separator') value = formatWithDelimiters(cents, 0, '.', ',');
      else if (placeholder === 'amount_with_apostrophe_separator') value = formatWithDelimiters(cents, 2, "'", '.');
      else value = formatWithDelimiters(cents, 2, ',', '.');
      return format.replace(match[0], value);
    }

    syncOfferPrices() {
      var fallbackVariant = this.currentVariant();
      if (!fallbackVariant) return;
      this.queryAll('[data-ai-ppo-offer]').forEach(function (offer) {
        var dimensions = this.offerDimensions(offer);
        var manualPrice = Number(offer.getAttribute('data-manual-price-cents')) || 0;
        var manualCompare = Number(offer.getAttribute('data-manual-compare-cents')) || 0;
        var selectedVariants = this.selectedMainVariants(offer);
        var hasSelectors = this.mainVariantRows(offer).length > 0;
        var variantForUnit = function (index) {
          if (!hasSelectors || !selectedVariants.length) return fallbackVariant;
          if (dimensions.variantMode === 'shared_variant') return selectedVariants[0];
          return selectedVariants[Math.min(index, selectedVariants.length - 1)] || fallbackVariant;
        };
        var basePrice = 0;
        var baseCompare = 0;
        for (var paidIndex = 0; paidIndex < dimensions.purchasedTier; paidIndex += 1) {
          basePrice += Number(variantForUnit(paidIndex).price || 0);
        }
        for (var unitIndex = 0; unitIndex < dimensions.physicalUnits; unitIndex += 1) {
          var unitVariant = variantForUnit(unitIndex);
          var unitPrice = Number(unitVariant.price || 0);
          var unitCompare = Number(unitVariant.compare_at_price || 0);
          baseCompare += unitCompare > unitPrice ? unitCompare : unitPrice;
        }
        var current = manualPrice > 0 ? manualPrice : basePrice;
        var compare = manualCompare > current
          ? manualCompare
          : (baseCompare > basePrice ? baseCompare : 0);
        var nowNode = this.query('[data-ai-ppo-price-now]', offer);
        var compareNode = this.query('[data-ai-ppo-price-compare]', offer);
        var unitNode = this.query('[data-ai-ppo-unit-price]', offer);
        var badgeNode = this.query('[data-ai-ppo-offer-badge]', offer);
        if (nowNode) nowNode.textContent = this.money(current);
        if (compareNode) {
          compareNode.textContent = compare > current ? this.money(compare) : '';
          compareNode.hidden = !(compare > current);
        }
        if (unitNode) unitNode.textContent = this.money(current / dimensions.physicalUnits) + ' / each';
        if (badgeNode) {
          var computedBadge = compare > current
            ? 'Save ' + Math.round((compare - current) * 100 / compare) + '%'
            : '';
          badgeNode.textContent = computedBadge;
          badgeNode.hidden = !computedBadge;
        }
      }, this);
    }

    selectVariantButton(button, event) {
      if (!button || button.disabled) return;
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      var group = button.closest('[data-ai-ppo-variant-buttons]');
      if (group) {
        this.queryAll('[data-ai-ppo-variant-button]', group).forEach(function (item) {
          var selected = item === button;
          item.setAttribute('aria-pressed', selected ? 'true' : 'false');
          item.classList.toggle('is-selected', selected);
        });
      }
      this.syncAddonVariant(button.closest('[data-ai-ppo-addon]'));
    }

    selectedVariantControl(row) {
      var select = this.query('[data-ai-ppo-variant-select]', row);
      if (select) return select.options[select.selectedIndex] || null;
      return this.query('[data-ai-ppo-variant-button][aria-pressed="true"]', row) ||
        this.query('[data-ai-ppo-variant-button]:not([disabled])', row);
    }

    syncAllAddons() {
      this.queryAll('[data-ai-ppo-addon]').forEach(function (row) {
        var selectedButton = this.query('[data-ai-ppo-variant-button][aria-pressed="true"]', row);
        if (selectedButton) selectedButton.classList.add('is-selected');
        this.syncAddonVariant(row);
      }, this);
    }

    syncAddonVariant(row) {
      if (!row) return;
      var control = this.selectedVariantControl(row);
      var checkbox = this.query('[data-ai-ppo-addon-toggle]', row);
      if (!control || !checkbox) return;
      var id = Number(control.getAttribute('data-variant-id') || control.value) || 0;
      var available = control.getAttribute('data-available') !== 'false' && !control.disabled && id > 0;
      checkbox.setAttribute('data-variant-id', available ? String(id) : '');
      checkbox.setAttribute('data-price-cents', control.getAttribute('data-price-cents') || '0');
      checkbox.setAttribute('data-compare-cents', control.getAttribute('data-compare-cents') || '0');
      checkbox.disabled = !available;
      if (!available) checkbox.checked = false;
      row.classList.toggle('is-disabled', !available);

      var current = Number(control.getAttribute('data-price-cents')) || 0;
      var compare = Number(control.getAttribute('data-compare-cents')) || 0;
      var currentNode = this.query('[data-ai-ppo-addon-price]', row);
      var compareNode = this.query('[data-ai-ppo-addon-compare]', row);
      var discountNode = this.query('[data-ai-ppo-addon-discount]', row);
      if (currentNode) currentNode.textContent = this.money(current);
      if (compareNode) {
        compareNode.textContent = compare > current ? this.money(compare) : '';
        compareNode.hidden = !(compare > current);
      }
      if (discountNode) {
        var discount = compare > current ? Math.round((compare - current) * 100 / compare) : 0;
        discountNode.textContent = discount > 0 ? 'SAVE ' + discount + '%' : '';
        discountNode.hidden = discount <= 0;
      }

      var preview = this.query('[data-ai-ppo-media]', row);
      var image = this.query('[data-ai-ppo-thumb]', row);
      var previewSrc = control.getAttribute('data-preview-src') || '';
      var thumbSrc = control.getAttribute('data-thumb-src') || previewSrc;
      if (preview) {
        if (previewSrc) {
          if (preview.hasAttribute('data-ai-ppo-preview')) preview.disabled = false;
          preview.setAttribute('data-preview-src', previewSrc);
          if (image) image.src = thumbSrc;
        } else {
          if (preview.hasAttribute('data-ai-ppo-preview')) preview.disabled = true;
          preview.removeAttribute('data-preview-src');
        }
      }
      this.syncAddonState(row);
    }

    syncAddonState(row) {
      if (!row) return;
      var checkbox = this.query('[data-ai-ppo-addon-toggle]', row);
      var selected = !!(checkbox && checkbox.checked && !checkbox.disabled);
      row.classList.toggle('is-selected', selected);
      var quantity = positiveNumber(row.getAttribute('data-addon-quantity'), 1);
      var max = positiveNumber(row.getAttribute('data-max-quantity'), 3);
      quantity = Math.max(1, Math.min(max, quantity));
      row.setAttribute('data-addon-quantity', String(quantity));
      var output = this.query('[data-ai-ppo-qty-value]', row);
      if (output) output.textContent = String(quantity);
      this.queryAll('[data-ai-ppo-qty-change]', row).forEach(function (button) {
        var direction = Number(button.getAttribute('data-ai-ppo-qty-change'));
        button.disabled = direction < 0 ? quantity <= 1 : quantity >= max;
      });
      this.syncAddonSummary(row.closest('[data-ai-ppo-addons]'));
      this.syncDynamicCheckout();
    }

    changeAddonQuantity(button) {
      var row = button.closest('[data-ai-ppo-addon]');
      if (!row) return;
      var direction = Number(button.getAttribute('data-ai-ppo-qty-change')) || 0;
      var quantity = positiveNumber(row.getAttribute('data-addon-quantity'), 1) + direction;
      var max = positiveNumber(row.getAttribute('data-max-quantity'), 3);
      row.setAttribute('data-addon-quantity', String(Math.max(1, Math.min(max, quantity))));
      this.syncAddonState(row);
    }

    syncAddonSummary(panel) {
      if (!panel) return;
      var count = this.queryAll('[data-ai-ppo-addon-toggle]:checked', panel).reduce(function (total, input) {
        if (input.disabled) return total;
        var row = input.closest('[data-ai-ppo-addon]');
        return total + positiveNumber(row && row.getAttribute('data-addon-quantity'), 1);
      }, 0);
      var summary = this.query('[data-ai-ppo-addon-summary]', panel);
      if (!summary) return;
      summary.textContent = count ? count + (count === 1 ? ' add-on selected' : ' add-ons selected') : '';
      summary.hidden = count === 0;
    }

    activeAddonItems() {
      var offer = this.activeOffer();
      if (!offer) return [];
      var items = [];
      this.queryAll('[data-ai-ppo-addon-toggle]:checked', offer).forEach(function (checkbox) {
        var id = Number(checkbox.getAttribute('data-variant-id')) || 0;
        var row = checkbox.closest('[data-ai-ppo-addon]');
        if (!id || checkbox.disabled || !row) return;
        items.push({
          id: id,
          quantity: positiveNumber(row.getAttribute('data-addon-quantity'), 1),
          label: checkbox.getAttribute('data-addon-label') || 'Add-on'
        });
      });
      return items;
    }

    syncDynamicCheckout() {
      if (!this.adapter) return;
      var shouldHide = !!this.config.hideDynamicCheckout && this.activeAddonItems().length > 0;
      this.adapter.setDynamicCheckoutHidden(this, this.form, shouldHide);
    }

    cartItems() {
      var offer = this.activeOffer();
      if (!offer) return [];
      var dimensions = this.offerDimensions(offer);
      var baseProperties = this.offerProperties(offer);
      var items = [];
      var mainRows = this.mainVariantRows(offer);
      if (mainRows.length) {
        var selectedVariants = this.selectedMainVariants(offer);
        if (selectedVariants.length !== mainRows.length) return [];
        if (dimensions.variantMode === 'shared_variant') {
          items.push({
            id: Number(selectedVariants[0].id),
            quantity: dimensions.cartQuantity,
            properties: this.cloneProperties(baseProperties)
          });
        } else {
          for (var index = 0; index < dimensions.cartQuantity; index += 1) {
            var selected = selectedVariants[Math.min(index, selectedVariants.length - 1)];
            if (!selected || selected.available === false) return [];
            var itemProperties = this.cloneProperties(baseProperties);
            itemProperties['Item number'] = String(index + 1);
            items.push({ id: Number(selected.id), quantity: 1, properties: itemProperties });
          }
        }
      } else {
        var variant = this.currentVariant();
        if (!variant || variant.available === false) return [];
        items.push({
          id: Number(variant.id),
          quantity: dimensions.cartQuantity,
          properties: this.cloneProperties(baseProperties)
        });
      }
      this.activeAddonItems().forEach(function (item) {
        items.push({
          id: Number(item.id),
          quantity: item.quantity,
          properties: {
            'Add-on': item.label,
            Offer: baseProperties.Offer
          }
        });
      });
      return items;
    }

    setSubmitting(active) {
      this.submitting = !!active;
      this.classList.toggle('is-submitting', this.submitting);
      if (!this.form) return;
      this.queryAll('button[type="submit"], input[type="submit"]', this.form).forEach(function (button) {
        if (active) {
          button.setAttribute('data-ai-ppo-was-disabled', button.disabled ? 'true' : 'false');
          button.disabled = true;
          button.setAttribute('aria-busy', 'true');
        } else if (button.hasAttribute('data-ai-ppo-was-disabled')) {
          button.disabled = button.getAttribute('data-ai-ppo-was-disabled') === 'true';
          button.removeAttribute('data-ai-ppo-was-disabled');
          button.removeAttribute('aria-busy');
        }
      });
    }

    showMessage(message, state) {
      var node = this.query('[data-ai-ppo-message]');
      if (!node) return;
      node.textContent = message || '';
      node.hidden = !message;
      if (state) node.setAttribute('data-state', state);
      else node.removeAttribute('data-state');
    }

    async submitMultiItem(paymentMethod) {
      if (this.submitting) return;
      this.ensureForm();
      var items = this.cartItems();
      if (!this.form) {
        this.showMessage(this.config.formMissingText || 'Product form not found. Check the installation point.', 'error');
        return;
      }
      if (!items.length) {
        this.showMessage(this.config.unavailableText || 'Choose an available option before adding to cart.', 'error');
        return;
      }

      this.setSubmitting(true);
      this.showMessage('', '');
      var surface = this.adapter ? this.adapter.getCartSurface() : null;
      var sections = this.adapter ? this.adapter.getCartSections(surface) : [];
      var body = { items: items };
      if (sections.length) {
        body.sections = sections.join(',');
        body.sections_url = window.location.pathname;
      }

      try {
        var response = await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(body)
        });
        var responseText = await response.text();
        var data;
        try {
          data = JSON.parse(responseText);
        } catch (parseError) {
          var verificationResponse = response.status === 403
            || response.status === 429
            || /connection needs to be verified|verifying your connection/i.test(responseText);
          if (verificationResponse) {
            throw new Error('Your connection needs to be verified. Turn off VPN or Private Browsing, reload the page, and try again.');
          }
          throw new Error(this.config.cartErrorText || 'Could not add to cart.');
        }
        if (!response.ok) throw new Error(data.description || data.message || this.config.cartErrorText);
        if (paymentMethod) {
          var routed = this.adapter && typeof this.adapter.routeQuickPayment === 'function'
            ? this.adapter.routeQuickPayment(paymentMethod)
            : false;
          if (!routed) {
            this.showMessage('Your items were added, but quick checkout is not ready. Open the cart to continue.', 'error');
            return;
          }
        } else if (this.adapter) {
          await this.adapter.afterAdd(data, {
            behavior: this.config.afterAdd || 'drawer',
            cartSurface: surface,
            picker: this
          });
        }
        this.showMessage(this.config.addedText || 'Added to cart.', 'success');
      } catch (error) {
        this.showMessage(error && error.message ? error.message : (this.config.cartErrorText || 'Could not add to cart.'), 'error');
      } finally {
        this.setSubmitting(false);
      }
    }

    openDialog(button) {
      var dialog = this.query('[data-ai-ppo-dialog]');
      var image = this.query('[data-ai-ppo-dialog-image]');
      var title = this.query('[data-ai-ppo-dialog-title]');
      var src = button.getAttribute('data-preview-src') || '';
      if (!dialog || !image || !src) return;
      this.lastDialogTrigger = button;
      image.src = src;
      image.alt = button.getAttribute('data-preview-title') || '';
      if (title) title.textContent = image.alt;
      this.bodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    }

    closeDialog() {
      var dialog = this.query('[data-ai-ppo-dialog]');
      if (!dialog) return;
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else {
        dialog.removeAttribute('open');
        this.afterDialogClose();
      }
    }

    afterDialogClose() {
      document.body.style.overflow = this.bodyOverflow || '';
      if (this.lastDialogTrigger && document.contains(this.lastDialogTrigger)) {
        this.lastDialogTrigger.focus();
      }
      this.lastDialogTrigger = null;
    }
  }

  customElements.define('ai-pdp-purchase-options-picker', AIPDPPurchaseOptionsPicker);
})();
